import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { DXONgSwiftUiModule } from '@dxo/ng-swift-ui';
import { BenefitsSharedModule } from '@benefits/shared';
import { BenefitsSharedFormsModule } from '@benefits/shared-forms';
import { BenefitsSharedServicesModule } from '@benefits/shared-services';
import { httpErrorInterceptorProviders } from '@benefitsui/app/interceptors/error/http-error.interceptor';
import { environment } from '@benefitsui/environments/environment';
import { AppEnvironment } from '@benefitsui/environments/environment.common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FooterComponent } from './components/footer/footer.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { NavPortalPageComponent } from './pages/nav-portal-page/nav-portal-page.component';
import { GoogleAnalyticsService } from './services/analytics/google-analytics.service';
import { PortalTimeoutModalComponent } from './shared/components/portal-timeout-modal/portal-timeout-modal.component';
import { PortalTimeoutInterceptor } from './shared/interceptors/portal-timeout/portal-timeout.interceptor';
import { ErrorPageComponent } from './pages/error-page/error-page.component';
import { NoAccessPageComponent } from './pages/no-access-page/no-access-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { ListPoliciesPageComponent } from './pages/list-policies-page/list-policies-page.component';
import { PolicyOverviewPageComponent } from './pages/policy-overview-page/policy-overview-page.component';
import { windowProvider } from './lib/tokens/window';
import { ReportPageComponent } from './pages/report-page/report-page.component';
import { httpPolicyTokenInterceptorProviders } from './interceptors/policy-token/http-policy-token.interceptor';
import { NoContentPageComponent } from './shared/components/no-content-page/no-content-page.component';
import { DocumentsPageComponent } from './pages/documents/documents-page/documents-page.component';
import { DocumentGroupComponent } from './pages/documents/document-group-page/document-group-page.component';
import { FlagsInterceptor } from './interceptors/flags/flags.interceptor';
import { XsrfInterceptor } from './interceptors/security/xsrf.interceptor';
import { DocumentsErrorPageComponent } from './shared/components/p8-documents-error-page/error-page.component';
import { BillingPageComponent } from './pages/billing-page/billing-page.component';
import { BillingListComponent } from './pages/billing-page/list/list.component';
import { BillingPaymentComponent } from './pages/billing-page/payment/payment.component';
import { PremiumComponent } from './pages/billing-page/premium/premium.component';
import { GetSortIconColorPipe } from './shared/pipes/getSortIconColor/getSortIconColor.pipe';
import { ScheduleAPageComponent } from './pages/scheduleA-page/scheduleA-page.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClaimPageComponent } from './pages/claim-page/claim-page.component';
import { EmployeePageComponent } from './pages/employee-page/employee-page.component';
import { EmployeeDetailComponent } from './pages/employee-page/detail/detail.component';
import { ListEmployeeComponent } from './pages/employee-page/list/list.component';
import { AddEmployeeComponent } from './pages/employee-page/add/add.component';
import { SearchBySsnComponent } from '@benefitsui/app/pages/employee-page/search-by-ssn/search-by-ssn.component';
import { LeftNavPortalPageComponent } from './pages/left-nav-portal-page/left-nav-portal-page.component';
import { ClaimsListComponent } from './pages/claim-page/list/claims-list.component';
import { ClaimDetailComponent } from './pages/claim-page/detail/detail.component';
import { CreateClaimComponent } from './pages/claim-page/create/create.component';
import { AcceptencePageComponent } from './shared/components/acceptence-page/acceptence-page.component';
import { AllFormsPageComponent } from './pages/form/forms-page.component';
import { ClaimsFormsPageComponent } from './pages/form/claims-page.component';
import { EmployeeFormsPageComponent } from './pages/form/employee-page.component';
import { TaxFormsPageComponent } from './pages/form/tax-page.component';
import { DashboardPageComponent } from './pages/dashboard-page/dashboard-page.component';
import { BenefitsCreateClaimComponent } from './pages/claim-page/benefits-create/benefits-create.component';
import { ContactsPageComponent } from './pages/contact-us/contacts-page.component';
import { EmptyResultsComponent } from './components/empty-results/empty-results.component';
import { ClaimsReportPageComponent } from './pages/claim-page/report/claims-report.component';

import { PageHeaderComponent } from './reusable-components/page-header/page-header.component';
import { BillingFancyPageComponent } from './pages/billing-fancy-page/billing-fancy-page.component';
import { BillingFancyStatementsListComponent } from './pages/billing-fancy-page/statements-list/statements-list.component';
import { StatementDetailsComponent } from './pages/billing-fancy-page/statement-details/statement-details.component';
import { RedirectFormsPageComponent } from './pages/form/redirect-page.component';
import { ReportViewerComponent } from './pages/claim-page/report-viewer/report-viewer.component';
import { BreadcrumbsComponent } from './reusable-components/breadcrumbs/breadcrumbs.component';
import { AutoBreadcrumbsDirective } from './reusable-components/breadcrumbs/auto-breadcrumbs.directive';
import { SubNavComponent } from './reusable-components/sub-navigation/sub-nav.component';
import { SubNavLayoutComponent } from './layouts/sub-nav-layout/sub-nav-layout.component';
import { ListCardComponent } from './reusable-components/list-card/list-card.component';
import { DemoLoginPageComponent } from './pages/demologin-page/demologin-page.component';

@NgModule({
  declarations: [
    AcceptencePageComponent,
    AddEmployeeComponent,
    AllFormsPageComponent,
    AppComponent,
    BillingListComponent,
    BillingPageComponent,
    BillingFancyPageComponent,
    BillingFancyStatementsListComponent,
    StatementDetailsComponent,
    BillingPaymentComponent,
    BenefitsCreateClaimComponent,
    BreadcrumbsComponent,
    AutoBreadcrumbsDirective,
    ClaimsListComponent,
    ClaimDetailComponent,
    ClaimPageComponent,
    ClaimsFormsPageComponent,
    ClaimsReportPageComponent,
    ContactsPageComponent,
    CreateClaimComponent,
    DashboardPageComponent,
    DocumentGroupComponent,
    DocumentsErrorPageComponent,
    DocumentsPageComponent,
    EmployeeDetailComponent,
    EmployeeFormsPageComponent,
    EmployeePageComponent,
    ErrorPageComponent,
    EmptyResultsComponent,
    FooterComponent,
    RedirectFormsPageComponent,
    GetSortIconColorPipe,
    HomePageComponent,
    LeftNavPortalPageComponent,
    ListCardComponent,
    ListEmployeeComponent,
    ListPoliciesPageComponent,
    LoginPageComponent,
    DemoLoginPageComponent,
    NavPortalPageComponent,
    NoAccessPageComponent,
    NoContentPageComponent,
    NotFoundPageComponent,
    PageHeaderComponent,
    PolicyOverviewPageComponent,
    PortalTimeoutModalComponent,

    PremiumComponent,
    ReportPageComponent,
    ReportViewerComponent,
    ScheduleAPageComponent,
    SearchBySsnComponent,
    SubNavComponent,
    SubNavLayoutComponent,
    TaxFormsPageComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    BenefitsSharedFormsModule,
    BenefitsSharedModule,
    HttpClientModule,
    ReactiveFormsModule,
    BenefitsSharedServicesModule.configure({
      sharedServicesEnvironmentType: AppEnvironment
    }),
    DXONgSwiftUiModule.forRoot({ analyticsService: GoogleAnalyticsService })
  ],
  providers: [
    {
      provide: AppEnvironment,
      useValue: environment
    },
    httpErrorInterceptorProviders,
    httpPolicyTokenInterceptorProviders,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XsrfInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PortalTimeoutInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FlagsInterceptor,
      multi: true
    },
    windowProvider
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule {}
